// NavigationStackContext.tsx
import React, {
  createContext,
  useContext,
  useLayoutEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getFocusedItemIndex, ReturnFocusRequest } from '../helpers/fieldFocusHelper';

export interface RouteState {
  from?: string;
  /** The `data-field` of the form field that opened this picker. */
  returnFocusTo?: string;
  /** Which list card had focus when this screen was left for another. */
  returnFocusToItem?: number;
  [key: string]: unknown;
}

interface RouteEntry {
  pathname: string;
  search?: string;
  state?: RouteState;
  timestamp: number;
}

type NavigationStack = RouteEntry[];

interface NavigationContextType {
  push: (path: string, state?: RouteState) => void;
  goBack: (overrideState?: RouteState, levels?: number) => void;
  goBackWithSelection: (overrideState?: RouteState) => void;
  takeReturnFocusRequest: () => ReturnFocusRequest | undefined;
  reset: (path: string, state?: RouteState) => void;
  stack: NavigationStack;
  clear: () => void;
  updateTop: (newState: RouteState) => void;
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

/** One step back refocuses what opened the screen: a picker's form field, else a list card. */
const buildReturnFocusRequest = (
  stack: NavigationStack,
  levels: number,
  moveToNextField: boolean,
): ReturnFocusRequest | undefined => {
  if (levels !== 1) return undefined;
  const field = stack[stack.length - 1]?.state?.returnFocusTo;
  if (field) return { field, moveToNextField };
  const itemIndex = stack[stack.length - 2]?.state?.returnFocusToItem;
  return itemIndex === undefined ? undefined : { itemIndex };
};

/** Notes the focused list card on the screen being left, for Back to return to. */
const rememberFocusedItem = (stack: NavigationStack) => {
  const current = stack[stack.length - 1];
  if (!current) return;
  current.state = { ...current.state, returnFocusToItem: getFocusedItemIndex() };
};

export const NavigationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const stackRef = useRef<NavigationStack>([]);
  const returnFocusRequestRef = useRef<ReturnFocusRequest | undefined>(undefined);

  // A layout effect so the entry exists before the new screen's own effects run; with useEffect,
  // a screen calling updateTop on mount would overwrite the screen it was opened from.
  useLayoutEffect(() => {
    const currentEntry: RouteEntry = {
      pathname: location.pathname,
      search: location.search,
      state: location.state as RouteState,
      timestamp: Date.now(),
    };

    const stack = stackRef.current;
    const last = stack[stack.length - 1];

    if (!last) {
      stack.push(currentEntry);
    } else if (last.pathname === currentEntry.pathname) {
      stack[stack.length - 1] = {
        ...last,
        ...currentEntry,
        state: { ...last.state, ...currentEntry.state },
        timestamp: Date.now(),
      };
    } else {
      stack.push(currentEntry);
    }
  }, [location]);

  const push = useCallback(
    (path: string, state?: RouteState) => {
      rememberFocusedItem(stackRef.current);
      navigate(path, { state });
    },
    [navigate],
  );

  const navigateBack = useCallback(
    (overrideState: RouteState | undefined, levels: number) => {
      const stack = stackRef.current;
      if (stack.length > 1) {
        stack.pop();
        let target: RouteEntry | undefined;
        for (let i = 0; i < levels && stack.length > 0; i++) {
          target = stack.pop();
        }
        if (target) {
          const finalState = { ...target.state, ...overrideState };
          navigate(`${target.pathname}${target.search ?? ''}`, {
            state: finalState,
          });
        }
      } else {
        navigate(-1);
      }
    },
    [navigate],
  );

  /** Backing out of a picker or a detail screen puts focus back on what opened it. */
  const goBack = useCallback(
    (overrideState?: RouteState, levels = 1) => {
      returnFocusRequestRef.current = buildReturnFocusRequest(stackRef.current, levels, false);
      navigateBack(overrideState, levels);
    },
    [navigateBack],
  );

  /** Leaving a picker with a value moves focus on to the field after the one that opened it. */
  const goBackWithSelection = useCallback(
    (overrideState?: RouteState) => {
      returnFocusRequestRef.current = buildReturnFocusRequest(stackRef.current, 1, true);
      navigateBack(overrideState, 1);
    },
    [navigateBack],
  );

  const takeReturnFocusRequest = useCallback(() => {
    const request = returnFocusRequestRef.current;
    returnFocusRequestRef.current = undefined;
    return request;
  }, []);

  const reset = useCallback(
    (path: string, state?: RouteState) => {
      const [pathname, search = ''] = path.split('?');
      stackRef.current = [{ pathname, search, state, timestamp: Date.now() }];
      navigate(path, { state });
    },
    [navigate],
  );
  const updateTop = useCallback((newState: RouteState) => {
    const stack = stackRef.current;
    if (stack.length > 0) {
      const last = stack[stack.length - 1];
      stack[stack.length - 1] = {
        ...last,
        state: { ...last.state, ...newState },
        timestamp: Date.now(),
      };
    }
  }, []);

  const clear = useCallback(() => {
    stackRef.current = [];
  }, []);
  const contextValue = useMemo(
    () => ({
      push,
      goBack,
      goBackWithSelection,
      takeReturnFocusRequest,
      reset,
      stack: stackRef.current,
      clear,
      updateTop,
    }),
    [
      push,
      goBack,
      goBackWithSelection,
      takeReturnFocusRequest,
      reset,
      clear,
      stackRef.current,
      updateTop,
    ],
  );

  return <NavigationContext.Provider value={contextValue}>{children}</NavigationContext.Provider>;
};

export const useNavigationStack = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigationStack must be used within NavigationProvider');
  }
  return context;
};
